import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import { selectAuthorizationPolicies, selectUser } from '@platform/platform.selectors';
import { AuthorizePolicies, AuthorizePolicy, CurrentUser, Permission, RequiredPermission } from '@platform/platform.state';

@Injectable({
  providedIn: 'root',
})
export class AuthorizeService {
  constructor(
    private store$: Store<RootState>,
    private logger: NGXLogger,
  ) {}

  private static substitutePermission(policy: string, permission: RequiredPermission, values: Map<string, string>): RequiredPermission {
    return {
      ...permission,
      resourceId: permission.resourceId.map((r) => {
        if (/^{.*}/g.test(r)) {
          const variableName = r.replace('{', '').replace('}', '');
          if (!values.has(variableName)) {
            throw new Error(`A value for the variable '${variableName}' is missing for policy ${policy}.`);
          }

          return values.get(variableName)!;
        }

        return r;
      }),
    };
  }

  private static anyPermissionMatches(permissions: Permission[], requiredPermission: RequiredPermission): boolean {
    return permissions.map((p) => AuthorizeService.matchPermission(p, requiredPermission)).findIndex((matched) => matched) >= 0;
  }

  private static matchPermission(permission: Permission, requiredPermission: RequiredPermission): boolean {
    if (permission.actions.findIndex((a) => a.toLowerCase() === requiredPermission.action.toLowerCase()) < 0) {
      return false;
    }

    for (let i = 0; i < requiredPermission.resourceId.length; i++) {
      if (permission.resourceId.length <= i) {
        return false;
      }

      if (permission.resourceId[i] === '**') {
        return true;
      }

      if (permission.resourceId[i] !== '*' && permission.resourceId[i].toLowerCase() !== requiredPermission.resourceId[i].toLowerCase()) {
        return false;
      }
    }

    return permission.resourceId.length === requiredPermission.resourceId.length;
  }

  public isAuthorized(policyName: AuthorizePolicies, values: Map<string, string>): Observable<boolean> {
    return combineLatest([this.store$.pipe(select(selectUser)), this.store$.pipe(select(selectAuthorizationPolicies))]).pipe(
      map(([user, policies]) => {
        if (!policies.has(policyName)) {
          throw new Error(`No policy with name '${policyName}' has been found`);
        }

        return { user: user?.data, policy: policies.get(policyName) };
      }),
      map(({ user, policy }) => this.authorizeUser(user, policy!, values)),
      first(),
    );
  }

  public hasPermissionsForPolicy(
    policyName: AuthorizePolicies,
    values: Map<string, string>,
    permissions: Permission[],
  ): Observable<boolean> {
    return this.store$.pipe(select(selectAuthorizationPolicies)).pipe(
      map((policies) => {
        const policy = policies.get(policyName);
        if (!policy) {
          throw new Error(`No policy with name '${policyName}' has been found`);
        }

        return policy.permissions
          .map((requiredPermission) => AuthorizeService.substitutePermission(policy.name, requiredPermission, values))
          .map((requiredPermission) => AuthorizeService.anyPermissionMatches(permissions, requiredPermission))
          .every((granted) => granted);
      }),
      first(),
    );
  }

  public getRequiredPermissionsForPolicy(policyName: AuthorizePolicies, values: Map<string, string>): Observable<RequiredPermission[]> {
    return this.store$.pipe(select(selectAuthorizationPolicies)).pipe(
      map((policies) => {
        const policy = policies.get(policyName);
        if (!policy) {
          throw new Error(`No policy with name '${policyName}' has been found`);
        }

        return policy.permissions.map((permission) => AuthorizeService.substitutePermission(policy.name, permission, values));
      }),
      first(),
    );
  }

  private authorizeUser(user: CurrentUser, policy: AuthorizePolicy, values: Map<string, string>): boolean {
    const isAuthorized =
      policy.permissions
        .map((p) => AuthorizeService.substitutePermission(policy.name, p, values))
        .map((p) => this.hasPermission(user, p))
        .findIndex((granted) => !granted) < 0;

    if (!isAuthorized) {
      this.logger.info(`The user is not authorized for policy ${policy.name}`, policy, user.permissions, values);
    }

    return isAuthorized;
  }

  private hasPermission(user: CurrentUser, requiredPermission: RequiredPermission): boolean {
    if (!AuthorizeService.anyPermissionMatches(user.permissions, requiredPermission)) {
      this.logger.trace('The user has no permission', requiredPermission, user.permissions);
    }

    return AuthorizeService.anyPermissionMatches(user.permissions, requiredPermission);
  }
}
